// Overview — fleet health rail, needs-attention table, recent alerts, count chips + KPIs.
// Fleet-wide money totals come from the server (already USD-converted); per-account values
// stay in native currency (mixed currencies are never summed on the client).

import { useMemo } from 'react';
import { C, MONO, SEV } from '../lib/tokens';
import { fmtAge, fmtMoney as money, fmtPctStr, hbTitle, fmtM } from '../lib/format';
import { toNum } from '../lib/decimal';
import { CHIP_BASE, mTd, mTh, mono } from '../lib/styles';
import { derive, type Derived } from '../lib/mappers';
import type { AccountRow } from '../types/api';
import { activeAlerts, activeCriticalCount } from '../lib/selectors';
import { Card, CardHead, EmptyState, H2, KpiTile, Sub, pnlColor } from '../components/common';
import { TradingVpsHealthBand } from '../components/TradingVpsHealthBand';
import { useData, useServerNow, useUi } from '../store/hooks';
import { navGo, openDrawer, uiStore, EMPTY_FILTERS } from '../store/uiStore';

export function Overview() {
  const accounts = useData((s) => s.accounts);
  const fleet = useData((s) => s.fleet);
  const alerts = useData((s) => s.alerts);
  const thr = useUi((s) => s.thr);
  const serverNow = useServerNow();

  const derivedMap = useMemo(() => {
    const m = new Map<string, Derived>();
    for (const a of accounts) m.set(a.id, derive(a, serverNow, thr));
    return m;
  }, [accounts, serverNow, thr]);

  const totals = fleet?.totals;
  const excluded = fleet?.unconverted_currencies ?? [];
  const conversionNote = excluded.length ? ' · excludes ' + excluded.join(', ') : '';
  const critAlerts = activeCriticalCount(alerts);
  const ddCount = accounts.filter((a) => {
    const d = derivedMap.get(a.id)!;
    return d.ddNum != null && d.ddNum > thr.dW;
  }).length;

  const chips: Array<{ g: string; c: string; label: string; n: number; conn?: string; k?: string; title: string }> = [
    { g: '●', c: C.gn, label: 'Live', n: totals?.live ?? 0, conn: 'live', title: 'Heartbeat within 10 s' },
    { g: '◐', c: C.am, label: 'Delayed', n: totals?.delayed ?? 0, conn: 'delayed', title: 'Heartbeat 10–60 s old' },
    { g: '◌', c: C.gy, label: 'Stale', n: totals?.stale ?? 0, conn: 'stale', title: 'Heartbeat 1–3 min old — values preserved' },
    { g: '✕', c: C.rd, label: 'Offline', n: totals?.offline ?? 0, conn: 'offline', title: 'No heartbeat for 3+ min or terminal down' },
    { g: '⊘', c: C.rd, label: 'Broker disc.', n: totals?.broker_disconnected ?? 0, k: 'bdis', title: 'Terminal alive but broker connection lost' },
  ];

  const attn = accounts
    .map((a) => [a, derivedMap.get(a.id)!] as const)
    .filter(([, d]) => d.rank < 3)
    .sort((p, q) => p[1].rank - q[1].rank || (q[1].eqNum ?? -Infinity) - (p[1].eqNum ?? -Infinity));

  const rail = buildRail(accounts, derivedMap);

  const recent = activeAlerts(alerts)
    .concat(alerts.filter((a) => a.state === 'acknowledged'))
    .slice()
    .sort((p, q) => Date.parse(q.started_at) - Date.parse(p.started_at))
    .slice(0, 6);

  const pageSub = fleet
    ? 'USD reporting · equity ' + fmtM(toNum(fleet.equity_total_usd), 'USD') + ' · floating ' + fmtM(toNum(fleet.floating_total_usd), 'USD', true) + conversionNote
    : 'Loading fleet summary…';

  return (
    <section data-screen-label="Overview" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Overview</h1>
        <Sub>{pageSub}</Sub>
      </div>

      <TradingVpsHealthBand />

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {chips.map((ch) => (
          <button
            key={ch.label}
            onClick={() =>
              uiStore.set({
                nav: 'accounts',
                f: { ...EMPTY_FILTERS, ...(ch.conn ? { conn: ch.conn } : {}) },
                quick: ch.k ? { [ch.k]: true } : {},
              })
            }
            title={ch.title}
            style={{ ...CHIP_BASE }}
          >
            <span style={{ fontFamily: MONO, color: ch.c }} aria-hidden="true">
              {ch.g}
            </span>
            <span>{ch.label}</span>
            <span style={{ fontFamily: MONO, fontWeight: 600, color: C.tx }}>{ch.n}</span>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(172px,1fr))', gap: 8 }}>
        <KpiTile label="Total equity" value={fleet ? fmtM(toNum(fleet.equity_total_usd), 'USD') : '—'} sub={(totals?.accounts ?? 0) + ' accounts · USD' + conversionNote} big title="Server-converted fleet equity" />
        <KpiTile label="Floating P&L" value={fleet ? fmtM(toNum(fleet.floating_total_usd), 'USD', true) : '—'} sub={(totals?.positions ?? 0) + ' open positions'} valueColor={pnlColor(fleet ? toNum(fleet.floating_total_usd) : null)} big title="Sum of open-position P&L, USD" />
        <KpiTile label="Exposure" value={(totals ? toNum(totals.open_lots) ?? 0 : 0).toFixed(1) + ' lots'} sub="total open volume" />
        <KpiTile label="Open positions" value={String(totals?.positions ?? 0)} sub={'across ' + accounts.filter((a) => a.positions_count > 0).length + ' accounts'} valueColor={(totals?.positions ?? 0) > 0 ? C.bl : undefined} />
        <KpiTile label="In drawdown" value={String(ddCount)} sub={'> ' + thr.dW + '% from equity peak'} valueColor={ddCount > 0 ? C.am : undefined} />
        <KpiTile label="Critical alerts" value={String(critAlerts)} sub={activeAlerts(alerts).length + ' active total'} valueColor={critAlerts > 0 ? C.rd : undefined} />
        <KpiTile label="Accounts" value={String(totals?.accounts ?? accounts.length)} sub="registered terminals" />
        <KpiTile label="Offline" value={String(totals?.offline ?? 0)} sub="no heartbeat > 3 min" valueColor={(totals?.offline ?? 0) > 0 ? C.rd : undefined} />
      </div>

      <Card>
        <CardHead>
          <H2>Fleet health</H2>
          <Sub>{rail.length + ' terminals · ' + accounts.length + ' accounts'}</Sub>
          <div style={{ flex: 1 }} />
          {[
            [C.gn2, 'Healthy'],
            [C.am, 'Warning / delayed'],
            [C.rd, 'Critical / offline'],
            [C.gy, 'Stale / unknown'],
            [C.bl, 'Syncing'],
          ].map(([col, label]) => (
            <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, color: C.dm }}>
              <span style={{ width: 9, height: 9, borderRadius: 2, background: col, display: 'inline-block' }} aria-hidden="true" />
              {label}
            </span>
          ))}
        </CardHead>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: '12px 14px' }}>
          {rail.map((cell) => (
            <button
              key={cell.key}
              onClick={() => openDrawer(cell.id)}
              title={cell.title}
              aria-label={cell.aria}
              style={{
                width: 20,
                height: 20,
                borderRadius: 4,
                border: '1px solid rgba(255,255,255,.07)',
                background: cell.color,
                color: '#0B0F18',
                fontSize: 10.5,
                fontFamily: MONO,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              {cell.glyph}
            </button>
          ))}
          {rail.length === 0 && <span style={{ fontSize: 12, color: C.dm }}>No terminals registered.</span>}
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ flex: '2.4 1 520px', minWidth: 340, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
          <CardHead>
            <H2>Needs attention</H2>
            <Sub>{attn.length + ' of ' + accounts.length + ' accounts'}</Sub>
            <div style={{ flex: 1 }} />
            <button onClick={() => navGo('accounts')} style={{ background: 'none', border: 'none', color: C.bl, fontSize: 11.5, padding: 0 }}>
              All accounts →
            </button>
          </CardHead>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Account', 'Conn', 'Broker link', 'Risk', 'Float P&L', 'DD%', 'Margin lvl', 'Heartbeat'].map((h, i) => (
                    <th key={h} scope="col" style={mTh(i >= 4 ? 'right' : 'left')}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {attn.slice(0, 9).map(([a, d]) => (
                  <tr key={a.id} onClick={() => openDrawer(a.id)} style={{ cursor: 'pointer' }}>
                    <td style={mTd('left', { fontWeight: 600 })} title={a.login_masked + ' · ' + a.broker}>
                      {a.label}
                    </td>
                    <td style={mTd('left', mono(d.cm[1]))} title={hbTitle(d.receivedAtMs, a.broker_tz_offset)}>
                      {d.cm[0] + ' ' + d.cm[2]}
                    </td>
                    <td style={mTd('left', mono(d.bm[1]))}>{d.bm[0] + ' ' + d.bm[2]}</td>
                    <td style={mTd('left', mono(d.rm[1]))} title={'Risk status'}>
                      {d.rm[0] + ' ' + (d.why || d.rm[2])}
                    </td>
                    <td style={mTd('right', mono(pnlColor(d.fltNum)))}>{money(a.floating_pl, a.currency, true)}</td>
                    <td style={mTd('right', mono(d.ddNum == null ? C.gy : d.ddNum > thr.dC ? C.rd : d.ddNum > thr.dW ? C.am : C.b9))}>{fmtPctStr(a.drawdown_pct)}</td>
                    <td style={mTd('right', mono(d.mlvlNum == null ? C.gy : d.mlvlNum < thr.mC ? C.rd : d.mlvlNum < thr.mW ? C.am : C.b9))}>
                      {d.mlvlNum == null ? 'Unavailable' : Math.round(d.mlvlNum) + '%'}
                    </td>
                    <td style={mTd('right', mono(d.cm[1]))} title={hbTitle(d.receivedAtMs, a.broker_tz_offset)}>
                      {fmtAge(d.hbAgeMs)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {attn.length === 0 && <div style={{ padding: 22, textAlign: 'center', fontSize: 12, color: C.dm }}>Nothing needs attention — fleet nominal.</div>}
        </div>

        <div style={{ flex: '1 1 300px', minWidth: 280, background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8 }}>
          <CardHead>
            <H2>Recent alerts</H2>
            <div style={{ flex: 1 }} />
            <button onClick={() => navGo('alerts')} style={{ background: 'none', border: 'none', color: C.bl, fontSize: 11.5, padding: 0 }}>
              Alerts →
            </button>
          </CardHead>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {recent.length === 0 && <div style={{ padding: 22, textAlign: 'center', fontSize: 12, color: C.dm }}>No alerts.</div>}
            {recent.map((al) => {
              const tok = SEV[al.severity]!;
              return (
                <button
                  key={al.id}
                  onClick={() => navGo('alerts')}
                  style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '9px 14px', background: 'none', border: 'none', borderBottom: '1px solid #16203A', textAlign: 'left' }}
                >
                  <span style={{ fontFamily: MONO, color: tok[1], fontSize: 11 }} aria-hidden="true">
                    {tok[0]}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 12, color: C.tx, fontWeight: 500 }}>{al.title}</span>
                    <span style={{ display: 'block', fontSize: 10.5, color: C.dm, marginTop: 1 }}>
                      {al.account_label + ' · ' + (al.state === 'acknowledged' ? 'acknowledged' : 'active')}
                    </span>
                  </span>
                  <span style={{ fontSize: 10, color: C.dm, fontFamily: MONO, whiteSpace: 'nowrap' }}>{fmtAge(serverNow - Date.parse(al.started_at))}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {accounts.length === 0 && fleet && (totals?.accounts ?? 0) === 0 && (
        <EmptyState title="No accounts connected" sub="The collector is reachable but no terminals are registered yet." />
      )}
    </section>
  );
}

interface RailCell {
  key: string;
  id: string;
  glyph: string;
  color: string;
  title: string;
  aria: string;
}

function buildRail(accounts: AccountRow[], derivedMap: Map<string, Derived>): RailCell[] {
  const entries: Array<[AccountRow, string]> = [];
  for (const a of accounts) {
    entries.push([a, a.terminal_id]);
    if (a.dup_terminal) entries.push([a, a.terminal_id + '-dup']);
  }
  entries.sort((x, y) => (x[1] < y[1] ? -1 : 1));
  return entries.map(([a, term]) => {
    const d = derivedMap.get(a.id)!;
    let color: string = C.gn2;
    let glyph = '';
    let status: string = d.cm[2];
    if (d.conn === 'offline') {
      color = C.rd;
      glyph = '✕';
      status = 'Offline';
    } else if (d.risk === 'critical') {
      color = C.rd;
      glyph = '!';
      status = 'Critical risk';
    } else if (d.bc === 'disconnected') {
      color = C.am;
      glyph = '⊘';
      status = 'Broker disconnected';
    } else if (d.conn === 'stale') {
      color = C.gy;
      status = 'Stale';
    } else if (d.risk === 'warning' || d.conn === 'delayed') {
      color = C.am;
      status = d.risk === 'warning' ? 'Warning' : d.cm[2];
    } else if (d.tr === 'sync') {
      color = C.bl;
      glyph = '⟳';
      status = 'Synchronizing';
    } else if (d.dq !== 'current' || d.tr === 'disabled' || d.tr === 'inactive') {
      color = C.gy;
      status = d.dq !== 'current' ? 'Partial data' : d.tm[2];
    }
    const dup = term.endsWith('-dup');
    return {
      key: a.id + ':' + term,
      id: a.id,
      glyph,
      color,
      title: term.replace('-dup', '') + ' · ' + a.label + ' — ' + status + (dup ? ' · duplicate terminal' : ''),
      aria: term.replace('-dup', '') + ' ' + a.label + ': ' + status,
    };
  });
}
